import { useState, useEffect, useRef, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { 
  Play, Square, RefreshCw, Terminal, CheckCircle, 
  XCircle, AlertTriangle, Server, Mail, ChevronDown, ChevronUp,
  ChevronLeft, ChevronRight
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { streamEmailCreation } from '@/services/api'
import { wsService } from '@/services/websocket'

/**
 * Email Creation Wizard with Real-Time Terminal
 * 
 * Features:
 * - Real-time command execution via WebSocket/SSE
 * - Live terminal output streaming
 * - Progress tracking per account
 * - Success/failure status for each email
 * - Ability to cancel/retry
 */
export function EmailCreationWizard({
  jobId,
  server,
  accounts = [],
  initialStatuses = {},
  onComplete,
  onCancel,
  onContinue,
  className
}) {
  // State
  const [isExecuting, setIsExecuting] = useState(false)
  const [isConnected, setIsConnected] = useState(false)
  const [showTerminal, setShowTerminal] = useState(true)
  const [lines, setLines] = useState([])
  const [progress, setProgress] = useState({
    current: 0,
    total: accounts.length,
    success: 0,
    failed: 0,
    pending: accounts.length
  })
  const [accountStatuses, setAccountStatuses] = useState({})
  const [error, setError] = useState(null)
  const [isComplete, setIsComplete] = useState(false)
  const [completionData, setCompletionData] = useState(null)
  
  const scrollRef = useRef(null)
  const streamRef = useRef(null)
  const outputEndRef = useRef(null)

  // Auto-scroll to bottom
  useEffect(() => {
    if (outputEndRef.current) {
      outputEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [lines])

  // Initialize account statuses — seed from persisted creation state so that
  // revisiting a completed batch shows already-created emails instead of resetting.
  useEffect(() => {
    const statuses = {}
    let created = 0
    accounts.forEach(acc => {
      const seed = initialStatuses[acc.email]
      const isCreated = seed && (seed.status === 'created' || seed.status === 'success')
      if (isCreated) created++
      statuses[acc.email] = {
        status: isCreated ? 'success' : 'pending',
        message: isCreated ? (seed.message || 'Already created') : undefined,
        output: [],
      }
    })
    setAccountStatuses(statuses)
    setProgress(prev => ({
      ...prev,
      total: accounts.length,
      success: created,
      pending: accounts.length - created,
    }))
  }, [accounts, initialStatuses])

  // Connect to WebSocket
  useEffect(() => {
    const connectWebSocket = async () => {
      try {
        await wsService.connect()
        setIsConnected(true)
        addLine('system', 'Connected to real-time terminal server')
      } catch (err) {
        console.error('WebSocket connection failed:', err)
        setIsConnected(false)
        addLine('warning', 'WebSocket unavailable, falling back to SSE streaming')
      }
    }

    connectWebSocket()

    return () => {
      if (streamRef.current) {
        streamRef.current.close()
      }
    }
  }, [])

  const addLine = (type, content, metadata = {}) => {
    setLines(prev => [...prev, {
      type,
      content: typeof content === 'string' ? content : JSON.stringify(content),
      timestamp: new Date().toLocaleTimeString(),
      id: `${Date.now()}-${Math.random()}`,
      ...metadata
    }])
  }

  const handleStreamMessage = useCallback((type, data) => {
    switch (type) {
      case 'init':
        addLine('info', '🚀 Starting email creation process...')
        addLine('info', `📧 Total accounts to create: ${accounts.length}`)
        addLine('info', `🖥️  Target server: ${server?.name} (${server?.host})`)
        break

      case 'progress':
        setProgress(prev => ({
          ...prev,
          current: data.current,
          total: data.total
        }))
        addLine('command', `[${data.current}/${data.total}] Creating email: ${data.email}`, { email: data.email })
        
        // Update account status
        setAccountStatuses(prev => ({
          ...prev,
          [data.email]: { ...prev[data.email], status: 'creating', output: [] }
        }))
        break

      case 'command':
        addLine('info', `${data.replace(/'[^']*'/g, "'***'")}`)
        break

      case 'output':
        if (typeof data === 'string') {
          addLine('stdout', data)
        } else if (data.email && data.line) {
          addLine('stdout', `  ${data.line}`, { email: data.email })
          setAccountStatuses(prev => ({
            ...prev,
            [data.email]: {
              ...prev[data.email],
              output: [...(prev[data.email]?.output || []), data.line]
            }
          }))
        }
        break

      case 'error':
        if (typeof data === 'string') {
          addLine('stderr', `  ✗ ${data}`)
        } else if (data.line) {
          addLine('stderr', `  ✗ ${data.line}`, { email: data.email })
        } else if (data.message) {
          addLine('stderr', `  ✗ ${data.message}`, { email: data.email })
        }
        break

      case 'success':
        addLine('success', `  ✓ ${data.message}`, { email: data.email })
        setProgress(prev => ({
          ...prev,
          success: prev.success + 1,
          pending: prev.pending - 1
        }))
        setAccountStatuses(prev => ({
          ...prev,
          [data.email]: { ...prev[data.email], status: 'success', message: data.message }
        }))
        break

      case 'failed':
        // Check if error is "already exists" - treat as success since email can be migrated
        const isAlreadyExists = data.message?.toLowerCase().includes('already exists') ||
                               data.message?.toLowerCase().includes('already created') ||
                               data.output?.toLowerCase().includes('already exists')
        
        // Check if error is password contains username
        const isPasswordContainsUsername = data.message?.toLowerCase().includes('password contains username')
        
        if (isAlreadyExists) {
          addLine('success', `  ✓ ${data.email} already exists (skipping creation)`, { email: data.email })
          setProgress(prev => ({
            ...prev,
            success: prev.success + 1,
            pending: prev.pending - 1
          }))
          setAccountStatuses(prev => ({
            ...prev,
            [data.email]: { ...prev[data.email], status: 'success', message: 'Already exists - skipped' }
          }))
        } else if (isPasswordContainsUsername) {
          addLine('error', `  ✗ ${data.email}: Password contains username - Plesk policy violation`, { email: data.email })
          addLine('info', `     💡 Solution: Change password on source server to not contain "${data.email.split('@')[0]}"`, { email: data.email })
          setProgress(prev => ({
            ...prev,
            failed: prev.failed + 1,
            pending: prev.pending - 1
          }))
          setAccountStatuses(prev => ({
            ...prev,
            [data.email]: { ...prev[data.email], status: 'failed', message: 'Password contains username - change required' }
          }))
        } else {
          addLine('error', `  ✗ ${data.message}`, { email: data.email })
          setProgress(prev => ({
            ...prev,
            failed: prev.failed + 1,
            pending: prev.pending - 1
          }))
          setAccountStatuses(prev => ({
            ...prev,
            [data.email]: { ...prev[data.email], status: 'failed', message: data.message }
          }))
        }
        break

      case 'complete':
        console.log('Email creation complete event received:', data)
        const hasFailures = data.failed > 0
        const allFailed = data.failed === data.total
        if (allFailed) {
          addLine('error', `\n❌ Email creation failed! All ${data.total} accounts failed.`)
        } else if (hasFailures) {
          addLine('warning', `\n⚠️ Email creation completed with issues: ${data.success} success, ${data.failed} failed`)
        } else {
          addLine('success', `\n✅ Email creation completed! All ${data.success} accounts created successfully.`)
        }
        addLine('info', `📊 Summary: ${data.success} success, ${data.failed} failed (Total: ${data.total})`)
        setIsExecuting(false)
        setIsComplete(true)
        setCompletionData(data)
        onComplete?.(data)
        break

      default:
        addLine('raw', data)
    }
  }, [accounts.length, server, onComplete])

  const startEmailCreation = useCallback(async () => {
    if (!jobId || !server?.id || isExecuting) return

    setIsExecuting(true)
    setError(null)
    setLines([])
    setProgress({
      current: 0,
      total: accounts.length,
      success: 0,
      failed: 0,
      pending: accounts.length
    })

    // Reset account statuses
    const statuses = {}
    accounts.forEach(acc => {
      statuses[acc.email] = { status: 'pending', output: [] }
    })
    setAccountStatuses(statuses)

    addLine('system', 'Initializing email creation stream...')

    try {
      // Use SSE streaming
      streamRef.current = streamEmailCreation(
        jobId,
        server.id,
        {
          onMessage: ({ type, data }) => handleStreamMessage(type, data),
          onError: (err) => {
            console.error('Stream error:', err)
            const errorMsg = err?.message || 'Connection failed - check if backend is running'
            setError('Stream connection failed: ' + errorMsg)
            addLine('error', 'Connection error: ' + errorMsg)
            setIsExecuting(false)
          },
          onComplete: () => {
            setIsExecuting(false)
            // Note: isComplete is set in handleStreamMessage 'complete' case
          }
        }
      )
    } catch (err) {
      console.error('Failed to start email creation:', err)
      setError(err.message)
      addLine('error', 'Failed to start: ' + err.message)
      setIsExecuting(false)
    }
  }, [jobId, server, accounts, isExecuting, handleStreamMessage])

  const cancelExecution = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.close()
      addLine('warning', '⏹️  Email creation cancelled by user')
      setIsExecuting(false)
      onCancel?.()
    }
  }, [onCancel])

  const getLineColor = (type) => {
    switch (type) {
      case 'command': return 'text-blue-400'
      case 'stdout': return 'text-green-400'
      case 'stderr': return 'text-red-400'
      case 'error': return 'text-red-500'
      case 'success': return 'text-green-500'
      case 'warning': return 'text-yellow-400'
      case 'info': return 'text-cyan-400'
      case 'system': return 'text-purple-400'
      default: return 'text-gray-300'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success': return <CheckCircle className="w-4 h-4 text-green-500" />
      case 'failed': return <XCircle className="w-4 h-4 text-red-500" />
      case 'creating': return <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />
      default: return <div className="w-4 h-4 rounded-full border-2 border-gray-300" />
    }
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case 'success': return <Badge variant="success" className="bg-green-500/20 text-green-600">Created</Badge>
      case 'failed': return <Badge variant="destructive">Failed</Badge>
      case 'creating': return <Badge variant="default" className="bg-blue-500 animate-pulse">Creating...</Badge>
      default: return <Badge variant="outline">Pending</Badge>
    }
  }

  // Derived creation counts — drives "Create Missing" vs. "already created" UI
  const createdCount = accounts.filter(a => {
    const s = accountStatuses[a.email]?.status
    return s === 'success' || s === 'created'
  }).length
  const missingCount = accounts.length - createdCount
  const allReady = accounts.length > 0 && missingCount === 0

  return (
    <div className={cn("space-y-4", className)}>
      {/* Header Card */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              <CardTitle>Email Account Creation</CardTitle>
            </div>
            <div className="flex items-center gap-2">
              {isConnected ? (
                <Badge variant="outline" className="text-green-600 border-green-600">
                  ● Real-time
                </Badge>
              ) : (
                <Badge variant="outline" className="text-yellow-600">
                  ○ Fallback Mode
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Server Info */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
            <Server className="w-4 h-4" />
            <span>Target: {server?.name || 'Not selected'}</span>
            <span className="text-gray-300">|</span>
            <span>{accounts.length} accounts</span>
          </div>

          {/* Already-created summary (revisiting a completed batch) */}
          {!isExecuting && createdCount > 0 && (
            <div className={cn(
              "flex items-center gap-2 text-sm rounded-md p-3 mb-4",
              allReady ? "bg-green-50 text-green-700" : "bg-blue-50 text-blue-700"
            )}>
              <CheckCircle className="w-4 h-4 flex-shrink-0" />
              <span>
                {createdCount} of {accounts.length} accounts already created
                {missingCount > 0 && ` · ${missingCount} still missing`}
              </span>
            </div>
          )}

          {/* Progress Bar */}
          {isExecuting && (
            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-sm">
                <span>Progress</span>
                <span>{progress.current} / {progress.total}</span>
              </div>
              <Progress 
                value={(progress.current / progress.total) * 100} 
                className="h-2"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span className="text-green-600">✓ {progress.success} success</span>
                <span className="text-red-600">✗ {progress.failed} failed</span>
                <span className="text-gray-500">○ {progress.pending} pending</span>
              </div>
            </div>
          )}

          {/* Error Alert */}
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertTriangle className="w-4 h-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Missing Data Warnings */}
          {(!jobId || !server?.id || accounts.length === 0) && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4">
              <div className="flex items-center gap-2 text-yellow-800 text-sm font-medium mb-1">
                <AlertTriangle className="w-4 h-4" />
                Missing required data
              </div>
              <ul className="text-xs text-yellow-700 space-y-1 ml-6">
                {!jobId && <li>• Job ID is missing</li>}
                {!server?.id && <li>• Destination server is not selected</li>}
                {accounts.length === 0 && <li>• No email accounts to create</li>}
              </ul>
            </div>
          )}

          {/* Control Buttons */}
          <div className="flex gap-2 flex-wrap">
            {isExecuting ? (
              <Button
                variant="destructive"
                onClick={cancelExecution}
                className="flex items-center gap-2"
              >
                <Square className="w-4 h-4" />
                Cancel
              </Button>
            ) : (isComplete || allReady) ? (
              /* Completion / already-ready actions */
              <>
                <Button
                  onClick={onContinue}
                  className="flex items-center gap-2"
                  disabled={!allReady && (!completionData || (completionData.failed === completionData.total && !completionData.skipped))}
                  title={!allReady && completionData?.failed === completionData?.total && !completionData?.skipped ? 'All emails failed - cannot proceed' : 'Proceed to migration'}
                >
                  Continue
                  <ChevronRight className="w-4 h-4" />
                </Button>
                {missingCount > 0 && (
                  <Button
                    variant="outline"
                    onClick={startEmailCreation}
                    disabled={!jobId || !server?.id}
                    className="flex items-center gap-2"
                  >
                    <Play className="w-4 h-4" />
                    Create Missing ({missingCount})
                  </Button>
                )}
                <Button
                  variant="ghost"
                  onClick={() => {
                    setIsComplete(false)
                    setCompletionData(null)
                    setLines([])
                    setAccountStatuses({})
                    setProgress({
                      current: 0,
                      total: accounts.length,
                      success: 0,
                      failed: 0,
                      pending: accounts.length
                    })
                  }}
                  className="flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Re-create All
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={startEmailCreation}
                  disabled={!jobId || !server?.id || accounts.length === 0}
                  className="flex items-center gap-2"
                  title={`jobId=${jobId}, server=${server?.id}, accounts=${accounts.length}`}
                >
                  <Play className="w-4 h-4" />
                  {createdCount > 0 ? `Create Missing (${missingCount})` : 'Start Email Creation'}
                </Button>

                {/* Skip to Migration button - allows bypassing email creation */}
                <Button
                  variant="outline"
                  onClick={() => {
                    // Mark all accounts as pre-existing/skipped
                    const skippedStatuses = accounts.map(acc => ({
                      email: acc.email,
                      status: 'success',
                      message: 'Skipped - assumed already exists'
                    }))
                    setAccountStatuses(
                      skippedStatuses.reduce((acc, curr) => {
                        acc[curr.email] = { status: 'success', message: curr.message }
                        return acc
                      }, {})
                    )
                    setCompletionData({
                      success: accounts.length,
                      failed: 0,
                      total: accounts.length,
                      skipped: true
                    })
                    setIsComplete(true)
                    addLine('warning', '⏭️  Skipping email creation - assuming emails already exist on destination')
                    addLine('info', `📧 ${accounts.length} accounts marked as ready for migration`)
                  }}
                  disabled={!jobId || !server?.id || accounts.length === 0}
                  className="flex items-center gap-2"
                  title="Skip email creation if you know emails already exist on the destination server"
                >
                  <ChevronRight className="w-4 h-4" />
                  Skip to Migration
                </Button>
              </>
            )}

            <Button
              variant="outline"
              onClick={() => setShowTerminal(!showTerminal)}
              className="flex items-center gap-2"
            >
              <Terminal className="w-4 h-4" />
              {showTerminal ? 'Hide' : 'Show'} Terminal
              {showTerminal ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
            
            {/* Back Button */}
            {!isExecuting && (
              <Button
                variant="ghost"
                onClick={onCancel}
                className="flex items-center gap-2 ml-auto"
              >
                <ChevronLeft className="w-4 h-4" />
                Back
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Terminal Output */}
      {showTerminal && (
        <Card className="bg-black border-gray-800">
          <CardHeader className="py-3 px-4 border-b border-gray-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-gray-400">
                <Terminal className="w-4 h-4" />
                <span className="text-sm font-mono">Live Terminal Output</span>
                {isExecuting && (
                  <RefreshCw className="w-3 h-3 animate-spin text-blue-500" />
                )}
              </div>
              <div className="flex items-center gap-2">
                {lines.length > 0 && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-6 text-xs text-gray-400 hover:text-white"
                    onClick={() => setLines([])}
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-80 px-4 py-3">
              {lines.length === 0 ? (
                <div className="text-gray-500 text-sm font-mono space-y-1">
                  <div>$ # Email Creation Terminal Ready</div>
                  <div>$ # Click "Start Email Creation" to begin</div>
                  <div>$ # Real-time output will appear here</div>
                </div>
              ) : (
                <div className="space-y-1 font-mono text-sm">
                  {lines.map((line) => (
                    <div 
                      key={line.id}
                      className={cn("break-all", getLineColor(line.type))}
                    >
                      {line.type === 'command' && <span className="text-gray-500">{line.timestamp} </span>}
                      {line.content}
                    </div>
                  ))}
                  {isExecuting && (
                    <div className="text-yellow-400 animate-pulse">{'>'} Executing...</div>
                  )}
                  <div ref={outputEndRef} />
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Account Status List */}
      {accounts.length > 0 && (
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm">Account Status</CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-48">
              <div className="space-y-2">
                {accounts.map((account) => {
                  const status = accountStatuses[account.email]
                  return (
                    <div 
                      key={account.email}
                      className="flex items-center justify-between p-2 rounded bg-muted/50"
                    >
                      <div className="flex items-center gap-2">
                        {getStatusIcon(status?.status)}
                        <span className="text-sm font-mono">{account.email}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {status?.message && (
                          <span className="text-xs text-muted-foreground truncate max-w-48">
                            {status.message}
                          </span>
                        )}
                        {getStatusBadge(status?.status)}
                      </div>
                    </div>
                  )
                })}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

export default EmailCreationWizard
